import { RefObject, useLayoutEffect, useState } from 'react';
import { rowOf, scrollPaneOf, settledHeight } from './scrollPane';

/** below this a body has no room to show anything — a short window scrolls instead */
const MIN_BODY_H = 280;

/**
 * The height an opened card's body takes so that the whole card — head, body
 * and borders — reaches `share` of the way down its scroll pane (see
 * useExpandedRow, which passes ROW_PANE_SHARE). Measured, so
 * it follows the window and the head's own height as either changes; null
 * until it has, so the body's CSS needs a stand-in for that first frame.
 *
 * One height whatever is in the body: more scrolls, less leaves space. A body
 * that sized itself to its contents would jump every time the reader changed
 * tabs, and a long one would push the next card off the page.
 */
const usePaneFitHeight = (
    cardRef: RefObject<HTMLElement>,
    headRef: RefObject<HTMLElement>,
    open: boolean,
    share: number,
    /** the list item the card sits in, where it is not the card — see rowOf */
    rowSelector?: string,
    /**
     * Leave this share of the next row in view below the card, instead of
     * reaching `share` of the pane — 0.5 for half of it. Without a next row,
     * `share` it is.
     */
    nextShare?: number,
) => {
    const [height, setHeight] = useState<number | null>(null);

    useLayoutEffect(() => {
        const card = cardRef.current;
        const head = headRef.current;
        if (!open || !card || !head) return undefined;
        const pane = scrollPaneOf(card);

        const measure = () => {
            const paneH = pane ? pane.clientHeight : window.innerHeight;
            // the row is what stops at its scroll-margin; the card may start a
            // little further down inside it
            const row = rowOf(card, rowSelector);
            const marginTop = parseFloat(window.getComputedStyle(row).scrollMarginTop) || 0;
            const inset = card.getBoundingClientRect().top - row.getBoundingClientRect().top;
            const borders = card.offsetHeight - card.clientHeight;
            const following = nextShare !== undefined ? row.nextElementSibling as HTMLElement | null : null;
            // how much of the pane is left under the card: the next row's
            // share and the gap before it, or what `share` leaves — the next
            // row as it will be once anything closing in it has gone (opening
            // this card closed it a moment ago)
            const below = following
                ? following.getBoundingClientRect().top - row.getBoundingClientRect().bottom
                    + settledHeight(following) * nextShare!
                : (1 - share) * paneH;
            const next = Math.round(paneH - below - marginTop - inset - head.offsetHeight - borders);
            setHeight(Math.max(MIN_BODY_H, next));
        };

        measure();
        window.addEventListener('resize', measure);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(head);
        if (pane) observer?.observe(pane);
        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [open, share, cardRef, headRef, rowSelector, nextShare]);

    return height;
};

export default usePaneFitHeight;
